import {
  FAO_MAJOR_FISHING_AREAS,
  FISHERY_GEAR_CATEGORIES,
  FISHERY_GEAR_CATEGORY_LABELS,
  FISHERY_PRODUCTION_METHOD_TERMS,
  FISHERY_PRODUCTION_METHODS,
  type FisheryGearCategory,
  type FisheryProductionMethod,
  faoAreaRequiresSubzone,
  faoSubzonesForArea,
  isFaoMajorFishingAreaCode,
} from './fishery-origin-catalog';
import {
  isPrepackagedMarketCountryCode,
  type PrepackagedMarketCountryCode,
  prepackagedCountryNameIt,
} from './prepackaged-country-names';

/** Reg. (UE) 1379/2013, artt. 35 and 38: https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:02013R1379-20200425 */
export enum PrepackagedVerticalOriginRegime {
  BEEF = 'BEEF',
  OTHER_MEAT_FRESH = 'OTHER_MEAT_FRESH',
  FISHERY = 'FISHERY',
  HONEY = 'HONEY',
  OLIVE_OIL = 'OLIVE_OIL',
  IT_MILK_DAIRY = 'IT_MILK_DAIRY',
  IT_DURUM_WHEAT_PASTA = 'IT_DURUM_WHEAT_PASTA',
  IT_RICE = 'IT_RICE',
  IT_TOMATO = 'IT_TOMATO',
  IT_PROCESSED_PORK = 'IT_PROCESSED_PORK',
  OTHER_REQUIRES_REVIEW = 'OTHER_REQUIRES_REVIEW',
}

export const PREPACKAGED_VERTICAL_ORIGIN_REGIMES = [
  PrepackagedVerticalOriginRegime.BEEF,
  PrepackagedVerticalOriginRegime.OTHER_MEAT_FRESH,
  PrepackagedVerticalOriginRegime.FISHERY,
  PrepackagedVerticalOriginRegime.HONEY,
  PrepackagedVerticalOriginRegime.OLIVE_OIL,
  PrepackagedVerticalOriginRegime.IT_MILK_DAIRY,
  PrepackagedVerticalOriginRegime.IT_DURUM_WHEAT_PASTA,
  PrepackagedVerticalOriginRegime.IT_RICE,
  PrepackagedVerticalOriginRegime.IT_TOMATO,
  PrepackagedVerticalOriginRegime.IT_PROCESSED_PORK,
  PrepackagedVerticalOriginRegime.OTHER_REQUIRES_REVIEW,
] as const;

export const PREPACKAGED_ORIGIN_AGGREGATES = [
  'UE',
  'NON_EU',
  'EU_AND_NON_EU',
  'EXTRA_EU',
  'EU_OR_EXTRA_EU',
] as const;

export type PrepackagedOriginAggregate = (typeof PREPACKAGED_ORIGIN_AGGREGATES)[number];
/**
 * Every field of this type stores an ISO 3166-1 alpha-2 code from
 * `PREPACKAGED_MARKET_COUNTRY_CODES`, or one of the aggregate pseudo-codes
 * above where the decree allows a "several States" shortcut — never free
 * text (D / task 4). `verticalOriginStatements` resolves the code to its
 * Italian name at print time through `prepackagedCountryNameIt`, the single
 * shared source — see `prepackaged-country-names.ts`.
 */
type CountryValue = PrepackagedMarketCountryCode | PrepackagedOriginAggregate;

export interface PrepackagedBeefOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.BEEF;
  readonly bornIn: CountryValue;
  readonly raisedIn: readonly CountryValue[];
  readonly slaughteredIn: CountryValue;
  readonly slaughterhouseApprovalNumber: string;
  readonly cutIn: CountryValue | null;
  readonly cuttingPlantApprovalNumber: string | null;
  /**
   * Reg. (CE) 1760/2000 art. 13 §2 lett. a): a number/code linking the meat
   * to the specific animal or group of animals. This is a LOT value, never a
   * recipe-template answer — it identifies one production run, and the
   * template describes every run made from this recipe. `null`/absent
   * resolves to the lot's own traceability code at generation time
   * (`PrepackagedVerticalOriginContext.lotReferenceCode`, resolved by the
   * caller from `Lot.lotNumber` — see `prepackaged-regulatory-facts.adapter.ts`
   * and `prepackaged-label-block-lines.ts`); a value stored HERE is the
   * operator explicitly overriding that default for this one lot, entered at
   * the lot itself via `PrepackagedLabelLotOverride.verticalOrigin`, never in
   * the recipe form.
   */
  readonly referenceCode?: string | null;
  readonly minced: boolean;
  readonly preparedIn: CountryValue | null;
  readonly thirdCountryInfoUnavailable: boolean;
}

export interface PrepackagedOtherFreshMeatOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.OTHER_MEAT_FRESH;
  readonly species: 'PIG' | 'SHEEP' | 'GOAT' | 'POULTRY';
  readonly raisedIn: readonly CountryValue[];
  readonly slaughteredIn: CountryValue;
  /**
   * Reg. di esecuzione (UE) 1337/2013: the batch code. Same lot-value
   * discipline as `PrepackagedBeefOriginEntry.referenceCode` above — resolves
   * from the lot's `lotNumber` at generation time, never typed into the
   * recipe template.
   */
  readonly batchCode?: string | null;
  readonly slaughterAgeMonths: number | null;
  readonly slaughterLiveWeightKg: number | null;
  readonly residenceThresholdMet: boolean;
  readonly mincedOrTrimmings: boolean;
  readonly thirdCountryInfoUnavailable: boolean;
}

export interface PrepackagedFisheryOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.FISHERY;
  readonly commercialName: string;
  readonly scientificName: string;
  readonly productionMethod: FisheryProductionMethod;
  readonly faoArea: string | null;
  readonly faoSubArea: string | null;
  readonly waterBody: string | null;
  readonly countryCode: PrepackagedMarketCountryCode | null;
  readonly gearCategory: FisheryGearCategory | null;
  readonly defrosted: boolean;
}

export interface PrepackagedHoneyCountry {
  readonly countryCode: PrepackagedMarketCountryCode;
  readonly sharePercent: number;
}

export interface PrepackagedHoneyOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.HONEY;
  readonly harvestCountries: readonly PrepackagedHoneyCountry[];
}

export interface PrepackagedOliveOilOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.OLIVE_OIL;
  readonly category: 'EXTRA_VIRGIN' | 'VIRGIN' | 'COMPOSED' | 'POMACE';
  readonly oliveHarvestPlace: string | null;
  readonly millPlace: string | null;
  readonly blend: 'SINGLE' | 'EU' | 'NON_EU' | 'EU_AND_NON_EU' | null;
  readonly protectedDesignation: string | null;
}

export interface PrepackagedMilkDairyOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.IT_MILK_DAIRY;
  readonly milkingCountry: CountryValue;
  readonly packagingOrProcessingCountry: CountryValue;
}

export interface PrepackagedDurumWheatPastaOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.IT_DURUM_WHEAT_PASTA;
  readonly wheatGrowingCountry: CountryValue;
  readonly millingCountry: CountryValue;
  readonly dominantWheatCountry?: CountryValue | null;
}

export interface PrepackagedRiceOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.IT_RICE;
  readonly growingCountry: CountryValue;
  readonly processingCountry: CountryValue;
  readonly packagingCountry: CountryValue;
}

export interface PrepackagedTomatoOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.IT_TOMATO;
  readonly growingCountry: CountryValue;
  readonly processingCountry: CountryValue;
}

export interface PrepackagedProcessedPorkOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.IT_PROCESSED_PORK;
  readonly bornIn: CountryValue;
  readonly raisedIn: CountryValue;
  readonly slaughteredIn: CountryValue;
  readonly processedInItaly: boolean;
}

export interface PrepackagedOtherVerticalOriginEntry {
  readonly regime: PrepackagedVerticalOriginRegime.OTHER_REQUIRES_REVIEW;
}

export type PrepackagedVerticalOriginEntry =
  | PrepackagedBeefOriginEntry
  | PrepackagedOtherFreshMeatOriginEntry
  | PrepackagedFisheryOriginEntry
  | PrepackagedHoneyOriginEntry
  | PrepackagedOliveOilOriginEntry
  | PrepackagedMilkDairyOriginEntry
  | PrepackagedDurumWheatPastaOriginEntry
  | PrepackagedRiceOriginEntry
  | PrepackagedTomatoOriginEntry
  | PrepackagedProcessedPorkOriginEntry
  | PrepackagedOtherVerticalOriginEntry;

export interface PrepackagedVerticalOriginContext {
  readonly netQuantityG?: number | null;
  /**
   * The lot's own traceability code, resolved by the caller (never derived
   * here — this module stays free of any notion of "lot"). BEEF's
   * `referenceCode` and OTHER_MEAT_FRESH's `batchCode` fall back to this
   * when the entry itself carries no explicit override. `null`/`undefined`
   * in a TEMPLATE context, where no lot exists yet.
   */
  readonly lotReferenceCode?: string | null;
}

export type PrepackagedVerticalOriginStatus =
  | 'READY'
  | 'TO_COMPLETE'
  | 'REQUIRES_SPECIALIST_REVIEW';

export interface PrepackagedVerticalOriginEvaluation {
  readonly status: PrepackagedVerticalOriginStatus;
  readonly statements: readonly string[];
  readonly missingFields: readonly string[];
}

/** Verbatim labels from the five Italian decrees, with legal placeholder text retained. */
export const IT_VERTICAL_ORIGIN_WORDINGS = {
  milkMilking: '«paese di mungitura»: nome del Paese',
  milkPackagingOrProcessing: '«Paese di condizionamento o di trasformazione»: nome del paese',
  milkSingleOrigin: '«origine del latte»: nome del Paese',
  durumGrowing: '«Paese di coltivazione del grano»: nome del Paese',
  durumMilling: '«Paese di molitura»: nome del Paese',
  riceGrowing: '«Paese di coltivazione del riso»: nome del Paese',
  riceProcessing: '«Paese di lavorazione»: nome del Paese',
  ricePackaging: '«Paese di confezionamento»: nome del Paese',
  riceSingleOrigin: '«origine del riso»: nome del paese',
  tomatoGrowing: '«Paese di coltivazione del pomodoro»: nome del Paese',
  tomatoProcessing: '«Paese di trasformazione del pomodoro»: nome del Paese',
  tomatoSingleOrigin: 'Origine del pomodoro: nome del Paese',
  porkBirth: '«Paese di nascita: (nome del paese di nascita degli animali)»',
  porkRaising: '«Paese di allevamento: (nome del paese di allevamento degli animali)»',
  porkSlaughter:
    '«Paese di macellazione: (nome del paese in cui sono stati macellati gli animali)»',
  porkSingleOrigin: '«Origine: (nome del paese)»',
  porkItalian: '«100% italiano»',
} as const;

export interface ItExperimentalOriginDecree {
  readonly regime:
    | PrepackagedVerticalOriginRegime.IT_MILK_DAIRY
    | PrepackagedVerticalOriginRegime.IT_DURUM_WHEAT_PASTA
    | PrepackagedVerticalOriginRegime.IT_RICE
    | PrepackagedVerticalOriginRegime.IT_TOMATO
    | PrepackagedVerticalOriginRegime.IT_PROCESSED_PORK;
  readonly decree: string;
  readonly url: string;
  readonly expiresOn: '2026-12-31';
}

/**
 * The five regimes are reported as expiring on 31 December 2026 by secondary
 * sources. The text of the official prorogation act is not accessible yet;
 * re-verify this field against the act before making expiry destructive.
 * Official act: https://www.gazzettaufficiale.it/eli/id/2026/02/11/26A00584/SG
 */
export const IT_EXPERIMENTAL_ORIGIN_DECREES: readonly ItExperimentalOriginDecree[] = [
  {
    regime: PrepackagedVerticalOriginRegime.IT_MILK_DAIRY,
    decree: 'Decreto interministeriale 9 dicembre 2016',
    url: 'https://www.gazzettaufficiale.it/eli/id/2017/01/19/17A00291/sg',
    expiresOn: '2026-12-31',
  },
  {
    regime: PrepackagedVerticalOriginRegime.IT_DURUM_WHEAT_PASTA,
    decree: 'Decreto interministeriale 26 luglio 2017',
    url: 'https://www.gazzettaufficiale.it/eli/id/2017/08/17/17A05704/sg',
    expiresOn: '2026-12-31',
  },
  {
    regime: PrepackagedVerticalOriginRegime.IT_RICE,
    decree: 'Decreto interministeriale 26 luglio 2017',
    url: 'https://www.gazzettaufficiale.it/eli/id/2017/08/16/17A05698/sg',
    expiresOn: '2026-12-31',
  },
  {
    regime: PrepackagedVerticalOriginRegime.IT_TOMATO,
    decree: 'Decreto interministeriale 16 novembre 2017',
    url: 'https://www.gazzettaufficiale.it/eli/id/2018/02/26/18A01366/sg',
    expiresOn: '2026-12-31',
  },
  {
    regime: PrepackagedVerticalOriginRegime.IT_PROCESSED_PORK,
    decree: 'Decreto interministeriale 6 agosto 2020',
    url: 'https://www.gazzettaufficiale.it/eli/id/2020/09/16/20A04874/sg',
    expiresOn: '2026-12-31',
  },
];

export const PREPACKAGED_VERTICAL_ORIGIN_LEGAL_SOURCES = {
  FISHERY: {
    act: 'Regolamento (UE) n. 1379/2013',
    article: 'artt. 35, 37, 38, 39',
    annex: 'Allegato III',
    url: 'https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:02013R1379-20200425',
  },
  HONEY: {
    act: 'D.Lgs. 30 dicembre 2025, n. 207',
    article: 'art. 3 comma 2',
    annex: null,
    url: 'https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:decreto.legislativo:2025-12-30;207',
  },
  IT_MILK_DAIRY: {
    act: 'Decreto interministeriale 9 dicembre 2016',
    article: 'artt. 2 e 3',
    annex: 'Allegato 1',
    url: 'https://www.gazzettaufficiale.it/eli/id/2017/01/19/17A00291/sg',
  },
  IT_DURUM_WHEAT_PASTA: {
    act: 'Decreto interministeriale 26 luglio 2017',
    article: 'artt. 2 e 3',
    annex: null,
    url: 'https://www.gazzettaufficiale.it/eli/id/2017/08/17/17A05704/sg',
  },
  IT_RICE: {
    act: 'Decreto interministeriale 26 luglio 2017',
    article: 'artt. 2 e 3',
    annex: null,
    url: 'https://www.gazzettaufficiale.it/eli/id/2017/08/16/17A05698/sg',
  },
  IT_TOMATO: {
    act: 'Decreto interministeriale 16 novembre 2017',
    article: 'artt. 2 e 3',
    annex: null,
    url: 'https://www.gazzettaufficiale.it/eli/id/2018/02/26/18A01366/sg',
  },
  IT_PROCESSED_PORK: {
    act: 'Decreto interministeriale 6 agosto 2020',
    article: 'art. 4',
    annex: null,
    url: 'https://www.gazzettaufficiale.it/eli/id/2020/09/16/20A04874/sg',
  },
  BEEF: {
    act: 'Reg. (CE) n. 1760/2000',
    article: 'artt. 13-14 (consolidato dal Reg. (UE) n. 653/2014)',
    annex: null,
    url: 'https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:02000R1760-20141213',
  },
  OTHER_MEAT_FRESH: {
    act: 'Reg. di esecuzione (UE) n. 1337/2013',
    article: 'artt. 5-7',
    annex: null,
    url: 'https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:32013R1337',
  },
  OLIVE_OIL: {
    act: 'Reg. delegato (UE) 2022/2104',
    article: 'art. 8',
    annex: null,
    url: 'https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:32022R2104',
  },
} as const;

const KNOWN_REGIMES: ReadonlySet<string> = new Set(PREPACKAGED_VERTICAL_ORIGIN_REGIMES);
const ORIGIN_AGGREGATE_VALUES: ReadonlySet<string> = new Set([
  ...PREPACKAGED_ORIGIN_AGGREGATES,
  'non UE',
  'UE e non UE',
  'extra UE',
  'UE o extra UE',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isKnownRegime(value: unknown): value is PrepackagedVerticalOriginRegime {
  return typeof value === 'string' && KNOWN_REGIMES.has(value);
}

/**
 * A shallow, structural narrow — `regime` is a known value, nothing else is
 * re-verified — for reading a persisted JSONB array back into
 * `PrepackagedVerticalOriginEntry[]`. Mirrors `readPackagingComponents`
 * (`backend/src/prepackaged-label/config/prepackaged-label-config.resolver.ts`):
 * a hand-edited row must not reach the composer/renderer as a half-built
 * entry, but full per-field domain validity stays
 * `evaluatePrepackagedVerticalOrigin`'s job, never duplicated here.
 */
export function isPrepackagedVerticalOriginEntry(
  value: unknown,
): value is PrepackagedVerticalOriginEntry {
  return isRecord(value) && isKnownRegime(value.regime);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * True only for a genuine ISO 3166-1 alpha-2 catalogue code, or one of this
 * field's allowed aggregate pseudo-codes — never an arbitrary string. Before
 * this check also required ISO membership, "Italia" / "italia" / "ITA" all
 * validated as a valid country value; only a stray aggregate token was
 * rejected (D / task 4).
 */
function fieldIsCountryValue(value: unknown, allowedAggregates: readonly string[]): boolean {
  if (!isNonEmptyString(value)) {
    return false;
  }
  if (ORIGIN_AGGREGATE_VALUES.has(value)) {
    return allowedAggregates.includes(value);
  }
  return isPrepackagedMarketCountryCode(value);
}

function aggregateTerm(value: string): string | null {
  switch (value) {
    case 'UE':
      return 'UE';
    case 'NON_EU':
    case 'non UE':
      return 'non UE';
    case 'EU_AND_NON_EU':
    case 'UE e non UE':
      return 'UE e non UE';
    case 'EXTRA_EU':
    case 'extra UE':
      return 'extra UE';
    case 'EU_OR_EXTRA_EU':
    case 'UE o extra UE':
      return 'UE o extra UE';
    default:
      return null;
  }
}

function quotedCountryStatement(label: string, country: string): string {
  return `«${label}»: ${country}`;
}

function quotedAggregateStatement(value: string): string {
  return `«${aggregateTerm(value) ?? value}»`;
}

function countryValue(record: Record<string, unknown>, fieldName: string): string | null {
  const value = record[fieldName];
  return isNonEmptyString(value) ? value : null;
}

function countryValueIsValid(
  record: Record<string, unknown>,
  fieldName: string,
  allowedAggregates: readonly string[],
): boolean {
  return fieldIsCountryValue(record[fieldName], allowedAggregates);
}

function missingCountryFields(
  record: Record<string, unknown>,
  fieldNames: readonly string[],
  allowedAggregates: readonly string[],
): string[] {
  return fieldNames.filter(
    (fieldName) => !countryValueIsValid(record, fieldName, allowedAggregates),
  );
}

function missingMilkFields(record: Record<string, unknown>): string[] {
  return missingCountryFields(
    record,
    ['milkingCountry', 'packagingOrProcessingCountry'],
    ['UE', 'NON_EU'],
  );
}

function missingDurumFields(record: Record<string, unknown>): string[] {
  return missingCountryFields(
    record,
    ['wheatGrowingCountry', 'millingCountry'],
    ['UE', 'NON_EU', 'EU_AND_NON_EU', 'non UE', 'UE e non UE'],
  );
}

function missingRiceFields(record: Record<string, unknown>): string[] {
  return missingCountryFields(
    record,
    ['growingCountry', 'processingCountry', 'packagingCountry'],
    ['UE', 'NON_EU', 'EU_AND_NON_EU', 'non UE', 'UE e non UE'],
  );
}

function missingTomatoFields(record: Record<string, unknown>): string[] {
  return missingCountryFields(
    record,
    ['growingCountry', 'processingCountry'],
    ['UE', 'NON_EU', 'EU_AND_NON_EU', 'non UE', 'UE e non UE'],
  );
}

function missingProcessedPorkFields(record: Record<string, unknown>): string[] {
  const missingFields = missingCountryFields(
    record,
    ['bornIn', 'raisedIn', 'slaughteredIn'],
    ['UE', 'EXTRA_EU', 'EU_OR_EXTRA_EU', 'extra UE', 'UE o extra UE'],
  );
  if (typeof record.processedInItaly !== 'boolean') {
    missingFields.push('processedInItaly');
  }
  return missingFields;
}

function beefRaisedInCountries(record: Record<string, unknown>): string[] | null {
  const raisedIn = record.raisedIn;
  if (!Array.isArray(raisedIn) || raisedIn.length === 0) {
    return null;
  }
  const countries = raisedIn.filter((country): country is string =>
    fieldIsCountryValue(country, []),
  );
  return countries.length === raisedIn.length ? countries : null;
}

function missingBeefCuttingFields(record: Record<string, unknown>): string[] {
  const cutInDeclared = record.cutIn !== null && record.cutIn !== undefined;
  const cuttingApprovalDeclared =
    record.cuttingPlantApprovalNumber !== null && record.cuttingPlantApprovalNumber !== undefined;
  if (!cutInDeclared && !cuttingApprovalDeclared) {
    return [];
  }
  const missingFields: string[] = [];
  if (!fieldIsCountryValue(record.cutIn, [])) {
    missingFields.push('cutIn');
  }
  if (!isNonEmptyString(record.cuttingPlantApprovalNumber)) {
    missingFields.push('cuttingPlantApprovalNumber');
  }
  return missingFields;
}

/**
 * Reg. (CE) n. 1760/2000, art. 13 §2 lett. a-c, §5 e art. 14:
 * https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:02000R1760-20141213
 * Art. 14 (carni bovine macinate) è citato dal solo brano riportato in
 * `docs/plans/prepackaged-D-origine.md` §2.1 («Preparato in: ...»); le
 * condizioni di dettaglio dello schema di semplificazione per il macinato
 * (che a memoria vivono nel regolamento di applicazione dell'art. 14, non
 * ancora verificato riga per riga) non sono modellate: quando `minced` è
 * vero si richiede solo `preparedIn`.
 */
/**
 * `referenceCode` is deliberately absent from this list: it is a lot value
 * (see the field's own doc-comment on `PrepackagedBeefOriginEntry`), never
 * required as a stored answer on the template. Whether a resolvable code
 * exists at generation time is a fact the caller supplies
 * (`facts.lotNumber`) and checks separately —
 * `prepackaged-regulatory.evaluator.ts`'s `verticalOriginStatusFor`, never
 * duplicated here.
 */
function missingBeefFields(record: Record<string, unknown>): string[] {
  const missingFields: string[] = [];
  if (record.minced === true) {
    if (!fieldIsCountryValue(record.preparedIn, [])) {
      missingFields.push('preparedIn');
    }
    return missingFields;
  }
  if (!fieldIsCountryValue(record.slaughteredIn, [])) {
    missingFields.push('slaughteredIn');
  }
  if (!isNonEmptyString(record.slaughterhouseApprovalNumber)) {
    missingFields.push('slaughterhouseApprovalNumber');
  }
  missingFields.push(...missingBeefCuttingFields(record));
  if (record.thirdCountryInfoUnavailable === true) {
    return missingFields;
  }
  if (!fieldIsCountryValue(record.bornIn, [])) {
    missingFields.push('bornIn');
  }
  if (beefRaisedInCountries(record) === null) {
    missingFields.push('raisedIn');
  }
  return missingFields;
}

/**
 * Reg. (CE) n. 1760/2000: la dicitura letterale è «Macellato in: [Stato
 * membro/paese terzo] [numero di approvazione]» e «Sezionato in: [Stato
 * membro/paese terzo] [numero di approvazione]» (art. 13 §2 lett. b-c),
 * «Origine: [Stato membro]» solo quando nascita, allevamento e macellazione
 * coincidono nello stesso Stato (art. 13 §5 lett. b), «Preparato in: [Stato]»
 * per il macinato (art. 14), «Origine: non UE» quando le informazioni sul
 * paese terzo non sono disponibili (art. 15). «Nato in»/«Allevato in» sono
 * prassi italiana, non testo di legge — vedi
 * `docs/plans/prepackaged-D-origine.md` §4.4, dove la scelta è documentata
 * come prassi e non come citazione. Il numero/codice di riferimento (§2
 * lett. a) non ha una dicitura letterale prescritta: si stampa il dato
 * grezzo.
 */
function beefBirthAndRearingStatements(
  record: Record<string, unknown>,
  slaughteredIn: string,
): string[] | null {
  if (record.thirdCountryInfoUnavailable === true) {
    return ['«Origine: non UE»'];
  }
  const bornIn = countryValue(record, 'bornIn');
  const raisedIn = beefRaisedInCountries(record);
  if (bornIn === null || raisedIn === null) {
    return null;
  }
  if (raisedIn.length === 1 && raisedIn[0] === bornIn && bornIn === slaughteredIn) {
    return [`«Origine: ${prepackagedCountryNameIt(bornIn)}»`];
  }
  return [
    `«Nato in: ${prepackagedCountryNameIt(bornIn)}»`,
    `«Allevato in: ${raisedIn.map(prepackagedCountryNameIt).join(', ')}»`,
  ];
}

function beefCuttingPlantStatement(record: Record<string, unknown>): string | null {
  const cutIn = countryValue(record, 'cutIn');
  const cuttingPlantApprovalNumber = record.cuttingPlantApprovalNumber;
  return cutIn !== null && isNonEmptyString(cuttingPlantApprovalNumber)
    ? `«Sezionato in: ${prepackagedCountryNameIt(cutIn)} ${cuttingPlantApprovalNumber}»`
    : null;
}

/**
 * The entry's own explicit override wins over the lot-resolved default —
 * "the answer owns the field": an operator who typed something specific for
 * this one lot must never see it silently replaced by the lot's generic
 * traceability code.
 */
function resolvedLotCode(
  explicitValue: unknown,
  context: PrepackagedVerticalOriginContext,
): string | null {
  if (isNonEmptyString(explicitValue)) {
    return explicitValue;
  }
  return isNonEmptyString(context.lotReferenceCode) ? context.lotReferenceCode : null;
}

function beefStatements(
  record: Record<string, unknown>,
  context: PrepackagedVerticalOriginContext,
): readonly string[] {
  const referenceCode = resolvedLotCode(record.referenceCode, context);
  const statements: string[] = referenceCode !== null ? [referenceCode] : [];
  if (record.minced === true) {
    const preparedIn = countryValue(record, 'preparedIn');
    return preparedIn === null
      ? statements
      : [...statements, `«Preparato in: ${prepackagedCountryNameIt(preparedIn)}»`];
  }
  const slaughteredIn = countryValue(record, 'slaughteredIn');
  const slaughterhouseApprovalNumber = record.slaughterhouseApprovalNumber;
  if (slaughteredIn === null || !isNonEmptyString(slaughterhouseApprovalNumber)) {
    return statements;
  }
  const originStatements = beefBirthAndRearingStatements(record, slaughteredIn);
  if (originStatements === null) {
    return statements;
  }
  statements.push(
    ...originStatements,
    `«Macellato in: ${prepackagedCountryNameIt(slaughteredIn)} ${slaughterhouseApprovalNumber}»`,
  );
  const cuttingStatement = beefCuttingPlantStatement(record);
  if (cuttingStatement !== null) {
    statements.push(cuttingStatement);
  }
  return statements;
}

function isMeatBlendAggregateValue(value: unknown): value is string {
  if (!isNonEmptyString(value)) {
    return false;
  }
  const term = aggregateTerm(value);
  return term === 'UE' || term === 'non UE' || term === 'UE e non UE';
}

function singleRaisedInValue(record: Record<string, unknown>): unknown {
  const raisedIn = record.raisedIn;
  return Array.isArray(raisedIn) && raisedIn.length === 1 ? raisedIn[0] : null;
}

function residenceAggregatePhrase(value: string): string | null {
  const term = aggregateTerm(value);
  switch (term) {
    case 'UE':
      return "vari Stati membri dell'UE";
    case 'non UE':
      return 'vari paesi terzi';
    case 'UE e non UE':
      return "vari Stati membri dell'UE e paesi terzi";
    default:
      return null;
  }
}

/**
 * Reg. di esecuzione (UE) n. 1337/2013:
 * https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:32013R1337
 * Art. 5 §1 lett. a) e b): «Allevato in» e «Macellato in» sono letterali.
 * Art. 5 §1 lett. a), soglie non raggiunte: «Allevato in: vari Stati membri
 * dell'UE» / «vari paesi terzi» / «vari Stati membri dell'UE e paesi terzi».
 * Art. 6: informazioni sul paese terzo non disponibili → «Allevato in: non
 * UE» + «Macellato in: (paese terzo)». Art. 7 (macinato e rifilature):
 * «Origine: UE» è ammessa come alternativa a «Allevato e macellato in: UE»
 * per lo stesso caso (entrambe letterali); qui si stampa solo la seconda per
 * mantenere una forma unica e deterministica.
 */
function missingOtherFreshMeatResidenceFields(record: Record<string, unknown>): string[] {
  const missingFields: string[] = [];
  const raisedCountry = singleRaisedInValue(record);
  if (!fieldIsCountryValue(raisedCountry, [])) {
    missingFields.push('raisedIn');
  }
  if (record.species !== 'PIG') {
    return missingFields;
  }
  if (typeof record.slaughterAgeMonths !== 'number') {
    missingFields.push('slaughterAgeMonths');
  }
  if (typeof record.slaughterLiveWeightKg !== 'number') {
    missingFields.push('slaughterLiveWeightKg');
  }
  return missingFields;
}

/**
 * `batchCode` is deliberately absent from this list — same lot-value
 * discipline as `missingBeefFields`'s `referenceCode` above.
 */
function missingOtherFreshMeatFields(record: Record<string, unknown>): string[] {
  const missingFields: string[] = [];
  if (record.thirdCountryInfoUnavailable === true) {
    if (!fieldIsCountryValue(record.slaughteredIn, [])) {
      missingFields.push('slaughteredIn');
    }
    return missingFields;
  }
  if (record.mincedOrTrimmings === true) {
    if (!isMeatBlendAggregateValue(singleRaisedInValue(record))) {
      missingFields.push('raisedIn');
    }
    if (!isMeatBlendAggregateValue(record.slaughteredIn)) {
      missingFields.push('slaughteredIn');
    }
    return missingFields;
  }
  if (!fieldIsCountryValue(record.slaughteredIn, [])) {
    missingFields.push('slaughteredIn');
  }
  missingFields.push(
    ...(record.residenceThresholdMet === true
      ? missingOtherFreshMeatResidenceFields(record)
      : isMeatBlendAggregateValue(singleRaisedInValue(record))
        ? []
        : ['raisedIn']),
  );
  return missingFields;
}

function otherFreshMeatStatements(
  record: Record<string, unknown>,
  context: PrepackagedVerticalOriginContext,
): readonly string[] {
  const batchCode = resolvedLotCode(record.batchCode, context);
  const statements: string[] = batchCode !== null ? [batchCode] : [];
  const slaughteredInAsIs = record.slaughteredIn;
  if (record.thirdCountryInfoUnavailable === true) {
    const slaughteredIn = countryValue(record, 'slaughteredIn');
    return slaughteredIn === null
      ? statements
      : [
          ...statements,
          '«Allevato in: non UE»',
          `«Macellato in: ${prepackagedCountryNameIt(slaughteredIn)}»`,
        ];
  }
  if (record.mincedOrTrimmings === true) {
    const raisedAggregate = singleRaisedInValue(record);
    if (
      !isMeatBlendAggregateValue(raisedAggregate) ||
      !isMeatBlendAggregateValue(slaughteredInAsIs)
    ) {
      return statements;
    }
    const raisedTerm = aggregateTerm(raisedAggregate);
    const slaughterTerm = aggregateTerm(slaughteredInAsIs);
    if (raisedTerm === slaughterTerm) {
      return [...statements, `«Allevato e macellato in: ${raisedTerm}»`];
    }
    return [...statements, `«Allevato in: ${raisedTerm}»`, `«Macellato in: ${slaughterTerm}»`];
  }
  const slaughteredIn = countryValue(record, 'slaughteredIn');
  if (slaughteredIn === null) {
    return statements;
  }
  const raisedValue = singleRaisedInValue(record);
  if (!isNonEmptyString(raisedValue)) {
    return statements;
  }
  const raisedLabel =
    record.residenceThresholdMet === true
      ? prepackagedCountryNameIt(raisedValue)
      : residenceAggregatePhrase(raisedValue);
  return raisedLabel === null
    ? statements
    : [
        ...statements,
        `«Allevato in: ${raisedLabel}»`,
        `«Macellato in: ${prepackagedCountryNameIt(slaughteredIn)}»`,
      ];
}

const OLIVE_OIL_CATEGORIES: ReadonlySet<string> = new Set([
  'EXTRA_VIRGIN',
  'VIRGIN',
  'COMPOSED',
  'POMACE',
]);

const OLIVE_OIL_BLEND_STATEMENTS: Record<'EU' | 'NON_EU' | 'EU_AND_NON_EU', string> = {
  EU: "«miscela di oli di oliva originari dell'Unione europea»",
  NON_EU: "«miscela di oli di oliva non originari dell'Unione europea»",
  EU_AND_NON_EU:
    "«miscela di oli di oliva originari dell'Unione europea e non originari dell'Unione»",
};

function oliveOilRequiresOrigin(category: unknown): boolean {
  return category === 'EXTRA_VIRGIN' || category === 'VIRGIN';
}

function isOliveOilBlendStatement(
  value: unknown,
): value is keyof typeof OLIVE_OIL_BLEND_STATEMENTS {
  return value === 'EU' || value === 'NON_EU' || value === 'EU_AND_NON_EU';
}

function singleOliveOilPlace(record: Record<string, unknown>, fieldName: string): string | null {
  return countryValueIsValid(record, fieldName, ['UE']) ? countryValue(record, fieldName) : null;
}

/**
 * Reg. delegato (UE) 2022/2104, art. 8: https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:32022R2104
 * Abroga dal 23 novembre 2022 il Reg. (UE) n. 29/2012 — vigenza verificata
 * su `docs/plans/prepackaged-D-origine.md` §2.5, non riletta indipendentemente
 * qui: se un atto successivo dovesse sostituirlo, questa citazione va
 * riverificata.
 * §1-2: l'origine è obbligatoria solo per l'extra vergine e il vergine;
 * nessuna origine per il composto di oli raffinati e vergini e per il sansa.
 * §3 lett. b): le tre diciture di miscela sono letterali con «originari» /
 * «non originari», non «provenienti» (smentisce l'assunto corrente, vedi la
 * stessa fonte). §3 lett. c): una DOP/IGP registrata sostituisce la
 * designazione. §3 lett. a) e §6: origine da un solo Stato/Unione/paese terzo
 * → riferimento diretto, senza una dicitura letterale prescritta (si stampa
 * il dato grezzo). §7: quando raccolta e frantoio sono in Stati diversi, la
 * formula è letterale; il testo del regolamento riporta «ottenuto in
 * [nell'Unione o in (Stato)]», concatenazione che produrrebbe «ottenuto in
 * nell'Unione» quando lo Stato è l'Unione — letta come ambiguità di
 * trascrizione e risolta qui senza il secondo «in» prima della parentesi
 * quadra; da riverificare sul testo pubblicato in Gazzetta UE.
 */
function missingOliveOilFields(record: Record<string, unknown>): string[] {
  if (!isNonEmptyString(record.category) || !OLIVE_OIL_CATEGORIES.has(record.category)) {
    return ['category'];
  }
  if (!oliveOilRequiresOrigin(record.category)) {
    return [];
  }
  if (isNonEmptyString(record.protectedDesignation)) {
    return [];
  }
  if (isOliveOilBlendStatement(record.blend)) {
    return [];
  }
  const missingFields: string[] = [];
  if (singleOliveOilPlace(record, 'oliveHarvestPlace') === null) {
    missingFields.push('oliveHarvestPlace');
  }
  if (singleOliveOilPlace(record, 'millPlace') === null) {
    missingFields.push('millPlace');
  }
  return missingFields;
}

function oliveOilPlacePhrase(place: string): string {
  return place === 'UE' ? "nell'Unione" : `in ${prepackagedCountryNameIt(place)}`;
}

function oliveOilStatements(record: Record<string, unknown>): readonly string[] {
  if (!oliveOilRequiresOrigin(record.category)) {
    return [];
  }
  const protectedDesignation = record.protectedDesignation;
  if (isNonEmptyString(protectedDesignation)) {
    return [protectedDesignation];
  }
  if (isOliveOilBlendStatement(record.blend)) {
    return [OLIVE_OIL_BLEND_STATEMENTS[record.blend]];
  }
  const oliveHarvestPlace = singleOliveOilPlace(record, 'oliveHarvestPlace');
  const millPlace = singleOliveOilPlace(record, 'millPlace');
  if (oliveHarvestPlace === null || millPlace === null) {
    return [];
  }
  if (oliveHarvestPlace === millPlace) {
    return [
      oliveHarvestPlace === 'UE' ? oliveHarvestPlace : prepackagedCountryNameIt(oliveHarvestPlace),
    ];
  }
  const categoryLabel = record.category === 'EXTRA_VIRGIN' ? 'extra vergine' : 'vergine';
  return [
    `«olio di oliva ${categoryLabel} ottenuto ${oliveOilPlacePhrase(millPlace)} da olive raccolte ${oliveOilPlacePhrase(oliveHarvestPlace)}»`,
  ];
}

function missingItalianFields(record: Record<string, unknown>): string[] {
  switch (record.regime) {
    case PrepackagedVerticalOriginRegime.IT_MILK_DAIRY:
      return missingMilkFields(record);
    case PrepackagedVerticalOriginRegime.IT_DURUM_WHEAT_PASTA:
      return missingDurumFields(record);
    case PrepackagedVerticalOriginRegime.IT_RICE:
      return missingRiceFields(record);
    case PrepackagedVerticalOriginRegime.IT_TOMATO:
      return missingTomatoFields(record);
    case PrepackagedVerticalOriginRegime.IT_PROCESSED_PORK:
      return missingProcessedPorkFields(record);
    default:
      return [];
  }
}

function isFisheryProductionMethod(value: unknown): value is FisheryProductionMethod {
  return typeof value === 'string' && FISHERY_PRODUCTION_METHODS.some((method) => method === value);
}

function isFisheryGearCategory(value: unknown): value is FisheryGearCategory {
  return (
    typeof value === 'string' && FISHERY_GEAR_CATEGORIES.some((category) => category === value)
  );
}

function missingCaughtFisheryFields(record: Record<string, unknown>): string[] {
  const missingFields: string[] = [];
  const faoArea = record.faoArea;
  if (!isNonEmptyString(faoArea) || !isFaoMajorFishingAreaCode(faoArea)) {
    missingFields.push('faoArea');
  } else if (faoAreaRequiresSubzone(faoArea) && !hasValidFaoSubzone(record, faoArea)) {
    missingFields.push('faoSubArea');
  }
  if (!isFisheryGearCategory(record.gearCategory)) {
    missingFields.push('gearCategory');
  }
  return missingFields;
}

function hasValidFaoSubzone(record: Record<string, unknown>, faoArea: string): boolean {
  const faoSubArea = record.faoSubArea;
  return (
    isNonEmptyString(faoSubArea) &&
    faoSubzonesForArea(faoArea).some((subzone) => subzone.code === faoSubArea)
  );
}

/**
 * `commercialName`/`scientificName` are free descriptive text, not country
 * fields — checked directly here rather than through `missingCountryFields`,
 * which enforces ISO catalogue membership and would wrongly reject them.
 */
function missingFisheryFields(record: Record<string, unknown>): string[] {
  const missingFields = ['commercialName', 'scientificName'].filter(
    (fieldName) => !isNonEmptyString(record[fieldName]),
  );
  const productionMethod = record.productionMethod;
  if (!isFisheryProductionMethod(productionMethod)) {
    missingFields.push('productionMethod');
    return missingFields;
  }
  if (productionMethod === 'CAUGHT') {
    return [...missingFields, ...missingCaughtFisheryFields(record)];
  }
  if (productionMethod === 'CAUGHT_FRESHWATER') {
    if (!isNonEmptyString(record.waterBody)) {
      missingFields.push('waterBody');
    }
    if (!countryValueIsValid(record, 'countryCode', [])) {
      missingFields.push('countryCode');
    }
    return missingFields;
  }
  return !countryValueIsValid(record, 'countryCode', [])
    ? [...missingFields, 'countryCode']
    : missingFields;
}

function missingHoneyFields(record: Record<string, unknown>): string[] {
  if (!Array.isArray(record.harvestCountries) || record.harvestCountries.length === 0) {
    return ['harvestCountries'];
  }
  const invalidCountry = record.harvestCountries.some(isInvalidHoneyCountry);
  return invalidCountry ? ['harvestCountries'] : [];
}

function isInvalidHoneyCountry(country: unknown): boolean {
  if (!isRecord(country)) {
    return true;
  }
  return (
    !isPrepackagedMarketCountryCode(country.countryCode) ||
    typeof country.sharePercent !== 'number' ||
    !Number.isFinite(country.sharePercent) ||
    country.sharePercent < 0 ||
    country.sharePercent > 100
  );
}

function missingFieldsForRecord(record: Record<string, unknown>): string[] {
  switch (record.regime) {
    case PrepackagedVerticalOriginRegime.FISHERY:
      return missingFisheryFields(record);
    case PrepackagedVerticalOriginRegime.HONEY:
      return missingHoneyFields(record);
    case PrepackagedVerticalOriginRegime.BEEF:
      return missingBeefFields(record);
    case PrepackagedVerticalOriginRegime.OTHER_MEAT_FRESH:
      return missingOtherFreshMeatFields(record);
    case PrepackagedVerticalOriginRegime.OLIVE_OIL:
      return missingOliveOilFields(record);
    case PrepackagedVerticalOriginRegime.IT_MILK_DAIRY:
    case PrepackagedVerticalOriginRegime.IT_DURUM_WHEAT_PASTA:
    case PrepackagedVerticalOriginRegime.IT_RICE:
    case PrepackagedVerticalOriginRegime.IT_TOMATO:
    case PrepackagedVerticalOriginRegime.IT_PROCESSED_PORK:
      return missingItalianFields(record);
    default:
      return [];
  }
}

function milkStatements(record: Record<string, unknown>): readonly string[] {
  const milkingCountry = countryValue(record, 'milkingCountry');
  const packagingOrProcessingCountry = countryValue(record, 'packagingOrProcessingCountry');
  if (milkingCountry === null || packagingOrProcessingCountry === null) {
    return [];
  }
  if (milkingCountry === packagingOrProcessingCountry && aggregateTerm(milkingCountry) === null) {
    return [`«origine del latte»: ${prepackagedCountryNameIt(milkingCountry)}`];
  }
  return [
    milkStatement('paese di mungitura', 'latte di Paesi', milkingCountry),
    milkStatement(
      'Paese di condizionamento o di trasformazione',
      'latte condizionato o trasformato in Paesi',
      packagingOrProcessingCountry,
    ),
  ];
}

function milkStatement(label: string, aggregateLabel: string, country: string): string {
  const aggregate = aggregateTerm(country);
  return aggregate === null
    ? quotedCountryStatement(label, prepackagedCountryNameIt(country))
    : `«${aggregateLabel} ${aggregate}»`;
}

function durumStatements(record: Record<string, unknown>): readonly string[] {
  return [
    ...italianCountryStatementOrAggregate(
      'Paese di coltivazione del grano',
      countryValue(record, 'wheatGrowingCountry'),
    ),
    ...italianCountryStatementOrAggregate(
      'Paese di molitura',
      countryValue(record, 'millingCountry'),
    ),
  ];
}

function riceStatements(record: Record<string, unknown>): readonly string[] {
  const countries = ['growingCountry', 'processingCountry', 'packagingCountry'].map((fieldName) =>
    countryValue(record, fieldName),
  );
  const firstCountry = countries[0];
  if (firstCountry !== null && countries.every((country) => country === firstCountry)) {
    if (aggregateTerm(firstCountry) === null) {
      return [`«origine del riso»: ${prepackagedCountryNameIt(firstCountry)}`];
    }
  }
  return [
    ...italianCountryStatementOrAggregate('Paese di coltivazione del riso', countries[0]),
    ...italianCountryStatementOrAggregate('Paese di lavorazione', countries[1]),
    ...italianCountryStatementOrAggregate('Paese di confezionamento', countries[2]),
  ];
}

function tomatoStatements(record: Record<string, unknown>): readonly string[] {
  const growingCountry = countryValue(record, 'growingCountry');
  const processingCountry = countryValue(record, 'processingCountry');
  if (growingCountry === null || processingCountry === null) {
    return [];
  }
  if (growingCountry === processingCountry && aggregateTerm(growingCountry) === null) {
    return [`Origine del pomodoro: ${prepackagedCountryNameIt(growingCountry)}`];
  }
  return [
    ...italianCountryStatementOrAggregate('Paese di coltivazione del pomodoro', growingCountry),
    ...italianCountryStatementOrAggregate(
      'Paese di trasformazione del pomodoro',
      processingCountry,
    ),
  ];
}

function processedPorkStatements(record: Record<string, unknown>): readonly string[] {
  const bornIn = countryValue(record, 'bornIn');
  const raisedIn = countryValue(record, 'raisedIn');
  const slaughteredIn = countryValue(record, 'slaughteredIn');
  if (bornIn === null || raisedIn === null || slaughteredIn === null) {
    return [];
  }
  if (bornIn === raisedIn && raisedIn === slaughteredIn && aggregateTerm(bornIn) === null) {
    const statements = [`«Origine: ${prepackagedCountryNameIt(bornIn)}»`];
    if (bornIn === 'IT' && record.processedInItaly === true) {
      statements.push('«100% italiano»');
    }
    return statements;
  }
  return [
    ...porkCountryStatement('Paese di nascita', bornIn),
    ...porkCountryStatement('Paese di allevamento', raisedIn),
    ...porkCountryStatement('Paese di macellazione', slaughteredIn),
  ];
}

function italianStatements(record: Record<string, unknown>): readonly string[] {
  switch (record.regime) {
    case PrepackagedVerticalOriginRegime.IT_MILK_DAIRY:
      return milkStatements(record);
    case PrepackagedVerticalOriginRegime.IT_DURUM_WHEAT_PASTA:
      return durumStatements(record);
    case PrepackagedVerticalOriginRegime.IT_RICE:
      return riceStatements(record);
    case PrepackagedVerticalOriginRegime.IT_TOMATO:
      return tomatoStatements(record);
    case PrepackagedVerticalOriginRegime.IT_PROCESSED_PORK:
      return processedPorkStatements(record);
    default:
      return [];
  }
}

function italianCountryStatementOrAggregate(
  label: string,
  country: string | null,
): readonly string[] {
  if (country === null) {
    return [];
  }
  return aggregateTerm(country) === null
    ? [quotedCountryStatement(label, prepackagedCountryNameIt(country))]
    : [quotedAggregateStatement(country)];
}

function porkCountryStatement(label: string, country: string): readonly string[] {
  return aggregateTerm(country) === null
    ? [`«${label}: ${prepackagedCountryNameIt(country)}»`]
    : [`«${label}: ${aggregateTerm(country)}»`];
}

function honeyStatements(
  record: Record<string, unknown>,
  context: PrepackagedVerticalOriginContext,
): readonly string[] {
  if (!Array.isArray(record.harvestCountries)) {
    return [];
  }
  const countries = record.harvestCountries.flatMap(honeyCountryFromUnknown);
  const orderedCountries = [...countries].sort(
    (firstCountry, secondCountry) => secondCountry.sharePercent - firstCountry.sharePercent,
  );
  const largestFourShare = orderedCountries
    .slice(0, 4)
    .reduce((totalShare, country) => totalShare + country.sharePercent, 0);
  const useFourCountryShortcut = orderedCountries.length > 4 && largestFourShare > 60;
  const useIsoCodes =
    context.netQuantityG !== null &&
    context.netQuantityG !== undefined &&
    context.netQuantityG < 30;
  return orderedCountries.map((country, index) =>
    honeyCountryStatement(country, index, useFourCountryShortcut, useIsoCodes),
  );
}

interface HoneyStatementCountry {
  readonly countryCode: string;
  readonly countryName: string;
  readonly sharePercent: number;
}

function honeyCountryFromUnknown(country: unknown): HoneyStatementCountry[] {
  if (!isRecord(country)) {
    return [];
  }
  const countryCode = country.countryCode;
  const sharePercent = country.sharePercent;
  if (
    !isPrepackagedMarketCountryCode(countryCode) ||
    typeof sharePercent !== 'number' ||
    !Number.isFinite(sharePercent)
  ) {
    return [];
  }
  return [
    {
      countryCode,
      countryName: prepackagedCountryNameIt(countryCode),
      sharePercent,
    },
  ];
}

function honeyCountryStatement(
  country: HoneyStatementCountry,
  index: number,
  useFourCountryShortcut: boolean,
  useIsoCodes: boolean,
): string {
  const countryLabel = useIsoCodes ? country.countryCode : country.countryName;
  return !useFourCountryShortcut || index < 4
    ? `${countryLabel}: ${country.sharePercent}%`
    : countryLabel;
}

function fisheryHeader(record: Record<string, unknown>): string[] | null {
  const commercialName = countryValue(record, 'commercialName');
  const scientificName = countryValue(record, 'scientificName');
  const productionMethod = record.productionMethod;
  if (
    commercialName === null ||
    scientificName === null ||
    !isFisheryProductionMethod(productionMethod)
  ) {
    return null;
  }
  return [
    `${commercialName} (${scientificName})`,
    FISHERY_PRODUCTION_METHOD_TERMS[productionMethod],
  ];
}

function caughtFisheryStatements(record: Record<string, unknown>): readonly string[] {
  const faoArea = countryValue(record, 'faoArea');
  if (faoArea === null) {
    return [];
  }
  const area = FAO_MAJOR_FISHING_AREAS.find((fishingArea) => fishingArea.code === faoArea);
  if (area === undefined) {
    return [];
  }
  const subzone = countryValue(record, 'faoSubArea');
  const subzoneName =
    subzone === null
      ? null
      : faoSubzonesForArea(faoArea).find((entry) => entry.code === subzone)?.nameIt;
  if (faoAreaRequiresSubzone(faoArea) && subzoneName === undefined) {
    return [];
  }
  const gearCategory = record.gearCategory;
  if (!isFisheryGearCategory(gearCategory)) {
    return [];
  }
  const areaStatement =
    subzoneName === null || subzoneName === undefined
      ? `${area.nameIt} (zona FAO ${area.code})`
      : `${subzoneName}, ${area.nameIt} (zona FAO ${area.code})`;
  return [areaStatement, FISHERY_GEAR_CATEGORY_LABELS[gearCategory]];
}

function freshwaterFisheryStatements(record: Record<string, unknown>): readonly string[] {
  const waterBody = countryValue(record, 'waterBody');
  const countryCode = countryValue(record, 'countryCode');
  return waterBody === null || countryCode === null
    ? []
    : [waterBody, prepackagedCountryNameIt(countryCode)];
}

function farmedFisheryStatements(record: Record<string, unknown>): readonly string[] {
  const countryCode = countryValue(record, 'countryCode');
  return countryCode === null ? [] : [prepackagedCountryNameIt(countryCode)];
}

function fisheryStatements(record: Record<string, unknown>): readonly string[] {
  const header = fisheryHeader(record);
  if (header === null) {
    return [];
  }
  switch (record.productionMethod) {
    case 'CAUGHT':
      return [...header, ...caughtFisheryStatements(record)];
    case 'CAUGHT_FRESHWATER':
      return [...header, ...freshwaterFisheryStatements(record)];
    case 'FARMED':
      return [...header, ...farmedFisheryStatements(record)];
    default:
      return [];
  }
}

export function verticalOriginMissingFields(
  entry: PrepackagedVerticalOriginEntry,
): readonly string[] {
  return evaluatePrepackagedVerticalOrigin(entry).missingFields;
}

export function verticalOriginStatements(
  entry: PrepackagedVerticalOriginEntry,
  context: PrepackagedVerticalOriginContext = {},
): readonly string[] {
  return evaluatePrepackagedVerticalOrigin(entry, context).statements;
}

function statementsForRecord(
  record: Record<string, unknown>,
  context: PrepackagedVerticalOriginContext,
): readonly string[] {
  switch (record.regime) {
    case PrepackagedVerticalOriginRegime.FISHERY:
      return fisheryStatements(record);
    case PrepackagedVerticalOriginRegime.HONEY:
      return honeyStatements(record, context);
    case PrepackagedVerticalOriginRegime.BEEF:
      return beefStatements(record, context);
    case PrepackagedVerticalOriginRegime.OTHER_MEAT_FRESH:
      return otherFreshMeatStatements(record, context);
    case PrepackagedVerticalOriginRegime.OLIVE_OIL:
      return oliveOilStatements(record);
    case PrepackagedVerticalOriginRegime.IT_MILK_DAIRY:
    case PrepackagedVerticalOriginRegime.IT_DURUM_WHEAT_PASTA:
    case PrepackagedVerticalOriginRegime.IT_RICE:
    case PrepackagedVerticalOriginRegime.IT_TOMATO:
    case PrepackagedVerticalOriginRegime.IT_PROCESSED_PORK:
      return italianStatements(record);
    default:
      return [];
  }
}

/**
 * `OTHER_REQUIRES_REVIEW` carries no fields (see
 * `PrepackagedOtherVerticalOriginEntry`): it means "no sector-specific
 * vertical-origin regime applies to this product", not "the rule pack
 * cannot compose this regime". It therefore resolves as READY with no
 * statements through the same two switches as every other regime, via their
 * `default` branches. `REQUIRES_SPECIALIST_REVIEW` is reserved for input
 * that is not a recognized regime at all (guarded above) — no member of
 * `PrepackagedVerticalOriginRegime` may produce it. See
 * `docs/plans/prepackaged-ERRATA-2026-08-20.md` §2 punto 3: the catalogues
 * missing regimes need get built, not routed around.
 */
export function evaluatePrepackagedVerticalOrigin(
  entry: unknown,
  context: PrepackagedVerticalOriginContext = {},
): PrepackagedVerticalOriginEvaluation {
  if (!isRecord(entry) || !isKnownRegime(entry.regime)) {
    return { status: 'REQUIRES_SPECIALIST_REVIEW', statements: [], missingFields: [] };
  }
  const missingFields = missingFieldsForRecord(entry);
  if (missingFields.length > 0) {
    return { status: 'TO_COMPLETE', statements: [], missingFields };
  }
  const statements = statementsForRecord(entry, context);
  return { status: 'READY', statements, missingFields: [] };
}

export function verticalOriginRequiresPrincipalFieldOfVision(
  regime: PrepackagedVerticalOriginRegime,
): boolean {
  return (
    regime === PrepackagedVerticalOriginRegime.HONEY ||
    regime === PrepackagedVerticalOriginRegime.IT_PROCESSED_PORK
  );
}

export function verticalOriginMinimumXHeightMm(
  regime: PrepackagedVerticalOriginRegime,
  largestSurfaceCm2: number | null,
): number | null {
  if (regime === PrepackagedVerticalOriginRegime.IT_MILK_DAIRY) {
    return null;
  }
  if (
    !KNOWN_REGIMES.has(regime) ||
    regime === PrepackagedVerticalOriginRegime.OTHER_REQUIRES_REVIEW
  ) {
    return null;
  }
  if (
    regime === PrepackagedVerticalOriginRegime.IT_PROCESSED_PORK &&
    largestSurfaceCm2 !== null &&
    largestSurfaceCm2 < 80
  ) {
    return 0.9;
  }
  return 1.2;
}

/**
 * The expiry is explicit data, not a clock lookup. The official prorogation
 * text is pending verification: https://www.gazzettaufficiale.it/eli/id/2026/02/11/26A00584/SG
 */
export function isPrepackagedVerticalOriginRegimeEffective(
  regime: PrepackagedVerticalOriginRegime,
  evaluationDate: string,
): boolean {
  if (!KNOWN_REGIMES.has(regime) || !/^\d{4}-\d{2}-\d{2}$/.test(evaluationDate)) {
    return false;
  }
  const decree = IT_EXPERIMENTAL_ORIGIN_DECREES.find((entry) => entry.regime === regime);
  return decree === undefined || evaluationDate <= decree.expiresOn;
}
